import cloneDeep from 'lodash/cloneDeep';
import isString from 'lodash/isString';
import type { AnalyticConfig} from './tenantModel';
import { defaultAnalyticConfig } from './tenantModel';
import { createUuid } from '../util/createUuid';
import { isObject } from '@mimir/primitive/type-guards/isObject';
import type { ItemMetaData } from './fieldValue';
import type { AwsCredentialIdentity } from '@smithy/types';
import type { ItemAccessControl } from './itemAccessControlDto';
import { getDefaultAccessControl } from './itemAccessControlUtil';

export interface BaseIngestConfigDto {
    /**
     * Identifier for the ingest config.
     *
     * Read only - except for when the client creates new ones.
     */
    id: string;

    /**
    * A name to show when presenting the ingest config
    */
    title: string,
    /**
     * Type of config. Possible values:
     * origin, ingest_media, ingest_person
     */
    ingestConfigType: IngestConfigTypes;
    /**
     * Allows a config item to be disabled.
     */
    isDisabled: boolean;
}

export interface IngestSourceStorageConfigDto extends BaseIngestConfigDto {
    /**
     * Storage provider of the current ingest storage. Available providers:
     * s3_native (AWS)
     * s3_clone
     * azure_blob
     */
    storageProvider: StorageProviders;
    /**
     * S3: Bucket name.
     * Azure: Storage account
     */
    sourceBucket: string;
    /**
     * Prefix/subfolder to apply when checking for matched content.
     * Might use '*' to indicate all content
     */
    sourceKeyPrefix?: string;

    /**
     * Type of access for the storage. Currently only relevant for AWS S3 storages.
     */
    accessType: AwsAccessType | undefined;
    /**
     * A string which the Mimir system will use to identify itself when asking to assume a role in a
     * customer AWS account in order to gain access to a customer S3 bucket. The customer will have an
     * IAM policy which verifies this identity before granting the role to the Mimir system.
     *
     * Since we're providing the customers with the IAM policy they need to use, we simply invent a
     * unique external identity for each customer S3 bucket that uses the assume role mechanism, and
     * present the customer with a suggested IAM policy that will permit this external identity when it
     * originates from our AWS account.
     * Currently only relevant for AWS S3 storages.
     */
    externalId: string | undefined;
}

export interface ExternalIngestSourceStorageConfigDto extends IngestSourceStorageConfigDto {
  /**
   * A reference to secret stored in secrets manager that holds required credentials
   * to access the storage
   */
  sourceBucketCredentials?: string;
  /**
   * External providers will require an endpoint to be specified.
   * E.g. http://myhost.mydns.com:9000/
   */
  sourceEndpoint?: string;

  /**
   * To store native/Clone S3 Access key /Azure storage account name
   */
  accessKey?: string;
}

export type SidecarParserTypes = 'mrss' | 'custom_xml' | 'custom_json' | 'vizOneXml';

export function isCustomSidecar(sidecarType: string | undefined) {
  if (!sidecarType) return false;
  return sidecarType === 'custom_xml' || sidecarType === 'custom_json';
}


export const SCAN_STORAGE_STATE_STALE_AFTER_MS = 5 * 60 * 1000;
export interface ScanStorageState {
  state: 'in_progress' | 'complete' | 'error';
  message: string;
  modifiedOn: string;
}

/**
 * Represents a file tag key-value pair for S3 object filtering
 */
export interface IngestFileTag {
  key: string;
  value: string;
}

export interface IngestStorageConfig extends IngestSourceStorageConfigDto, ExternalIngestSourceStorageConfigDto {
  /**
   * File extensions that will trigger an ingest.
   */
  ingestFileExtensions?: string; // '.mp4,.m4v'
  /**
   * Tags for filters
   */
  ingestFileTags?: IngestFileTag[];
  /**
   * This variable indicates whether all tags must match, or if any single tag match is sufficient
   */
  ingestIfAnyTagsMatch?: boolean;
  /**
   * When using an import workflow, this property can be used to specify a
   * subfolder inside the S3 gateway bucket where to store the highres files
   * handled by Kelda. This way, we can distinguish between highres files
   * that only exist on-premise and that must not be deleted and files that were
   * downloaded by a storage manager and that can be deleted to save disk space.
   */
  highresFolder?: string;
  /**
   * Mimir folder to use as base folder when building target path for the new item.
   */
  targetFolder?: string; // 'ingest-test';
  /**
   * Defines if we should remove the path defined by sourceKeyPrefix when creating
   * target path for the item.
   */
  targetFolderRemoveSourcePrefix?: boolean;
  /**
   * Defines if the ingest process requires a sidecar file to be present before
   * starting ingest.
   */
  requireSidecar?: boolean;
  /**
   * Identifier of a sidecar parser to be used when receiving sidecars for ingest.
   * Available ids:
   * mrss: Parses a Media RSS formatted sidecar file
   */
  sidecarParser?: string; // mrss
  /**
   * Expected filename for the sidecar. Either a fixed name or template based.
   * If assets are delivered in separate folders it is possible to use the fixed
   * naming standard to define the sidecar. E.g. vimondio_mrss.mrss
   * If the sidecar follows the same naming as the video it is also possible to use
   * a template to define sidecar file. {fileName} will bew replaced by base name
   * of the video file. E.g. If sidecarFileName is {fileName}.mrss and video file
   * is videoFile123.mp4, we will expect sidecar yo have filename: videoFile123.mrss
   */
  sidecarFileName?: string; // {source}.xml | sidecar.xml | vimondio_mrss.mrss
  /**
   * Defines confidence level for face detection
   */
  confidence?: number;
  /**
   * A value indicating timestamp of most recent ingested content.
   */
  sourceLastModified?: string;
  analyticConfig: AnalyticConfig;

  /**
   * If true, we try to delete from the bucket when an item is permanently deleted.
   * This requires the credentials to have delete permission.
   */
  deleteEnabled: boolean | undefined;

  /**
   * Used to be saved on the default upload location configuration in the tenant configuration.
   */
  enableS3Acceleration?: boolean;

  /**
   * Whether items ingested through this configuration are stored as 'proxy only' items. These items
   * have no highres and don't get processed, but they are preview-able in the browser (if browser supports
   * previewing the media type).
   */
  isProxyOnlyIngestConfig?: boolean;

  defaultMetadata?: ItemMetaData;
  defaultTimedMetadata?: ItemMetaData;

  scanStorageState?: ScanStorageState;

  // Items ingested using the ingest config will inherit this visibleTo
  visibleTo?: ItemAccessControl;
  /**
   * stores array of groups id which has the permission to see the location
   * As visibleTo property already exists, hence added GroupList for at the end
   */
  visibleToGroupList?: string[] | null;

  /**
   * If true, the ingest process will skip transcoding and other processing steps.
   *
   * This is reused for upload bucket configurations where we do not attach any triggers
   * to the bucket if this flag is set. This is for use in conjunction with kelda where
   * kelda will upload highres to the cloud and process (generate proxy etc) them locally
   * as well.
   */
  skipProcessing?: boolean;
}

export interface InternalIngestStorageConfigDto extends IngestStorageConfig {
  tenantId: string;
}

export interface CreateExternalIngestStorageConfigDto extends IngestStorageConfig {
  accessKeyId?: string;
  secretKey?: string;
}

export interface CleanCopyConformSettings {
  disableOverride?: boolean;
  autoConform?: boolean;
  tracksToMute?: string;
  preset?: string;
}

export type IngestStorageConfigClientDto = Omit<CreateExternalIngestStorageConfigDto, 'sourceBucketCredentials'> & {
  areCredentialsSaved?: boolean;
  accessKey?: string;
  awsAccountId?: string;
};

/**
 * These are the only properties that are mutable on upload location dto's.
 */
export interface UpdateUploadLocationDto {
  accessKey?: string;
  secretKey?: string;
  enableS3Acceleration?: boolean;
  deleteEnabled?: boolean;
  // This is mostly here to have a safe typeguard to distinguish between this type of update vs old fashioned sending the entire dto over.
  type: 'default_upload_location';
  // Backwards compatibility safety hatch, if for some reason we need to update an old minio configs' endpoint for the few legacy customers that run on this.
  sourceEndpoint?: string;
  pathPrefix?: string;
  visibleToGroupList: string[] | null | undefined;
  accessType: AwsAccessType | undefined;
  externalId: string | undefined;
}

export interface WorkerConfiguration {
  // How many Kelda workers should we have per Kelda instance ?
  fileWatchers?: number;
  preprocessors?: number;
  ingesters?: number;
  conformWorkers?: number;
}

export const isUpdateUploadLocationDto = (obj: unknown): obj is UpdateUploadLocationDto => {
  if (!isObject(obj)) return false;
  return 'type' in obj && obj.type === 'default_upload_location';
};

export const isBaseIngestConfig = (obj: unknown): obj is IngestSourceStorageConfigDto => {
  if (!isObject(obj)) return false;
  return 'ingestConfigType' in obj;
};
export const isPersonIngestConfig = (obj: BaseIngestConfigDto): obj is IngestSourceStorageConfigDto & { ingestConfigType: 'ingest_persons' } => {
  if (!obj) return false;
  return obj.ingestConfigType === 'ingest_persons';
};
export const isOriginConfig = (obj: BaseIngestConfigDto): obj is IngestSourceStorageConfigDto => {
  if (!obj) return false;
  return obj.ingestConfigType === 'origin';
};
export const isIngestStorageConfig = (obj: BaseIngestConfigDto): obj is IngestStorageConfig => {
  if (!obj) return false;
  const { ingestConfigType } = obj;
  return ingestConfigType === 'ingest_persons' ||
         ingestConfigType === 'default_upload_location' ||
         ingestConfigType === 'ingest_media';
};
export const isCreateExternalIngestConfig = (obj: BaseIngestConfigDto): obj is CreateExternalIngestStorageConfigDto => {
  if (!isIngestStorageConfig(obj) && !isOriginConfig(obj)) {
    return false;
  }

  const possibleDto = obj as Partial<CreateExternalIngestStorageConfigDto>;
  const accessKeyCheck = (possibleDto.storageProvider === 'gcs' || possibleDto.accessKeyId);
  const secretKeyCheck = (possibleDto.accessType === 'assume-role' && possibleDto.externalId) || possibleDto.secretKey;
  return Boolean(accessKeyCheck && secretKeyCheck);
};

export type StorageProviders = 's3_native' | 's3_clone' | 'azure_blob' | 'gcs';
export type IngestConfigTypes = 'undefined' | 'ingest_media' | 'ingest_persons' | 'origin' | 'default_upload_location';


// Default duration to wait before giving up on a P2 remux/stitch request
export const DEFAULT_P2_REQUEST_TIMEOUT_IN_SECONDS = 2 * 3600;

// Default duration to wait before garbage collecting a P2 folder without activity
export const DEFAULT_P2_GARBAGE_COLLECTION_THRESHOLD = 5 * 3600;

// Default duration the rendering engine has to pick up a render job
export const DEFAULT_CONFORM_MAX_IDLE_TIME_IN_MS = 5 * 60 * 1000;

// Default number of retries on Kelda errors
export const DEFAULT_MAX_RETRIES = 5;

// Default duration a file size must remain constant before considering
// that the file has stopped growing
export const DEFAULT_STABILITY_THRESHOLD_IN_MS = 20 * 1000;

// Default duration that a P2 folder must be stable before we can consider that all the
// files have arrived. It is only when all the known clips have been remuxed and this
// delay has elapsed without any file modification in the folder that the stitching will take place.
export const DEFAULT_P2_SINGLE_ITEM_STABILITY_THRESHOLD_IN_MS = 60 * 1000;

// Default value controlling how frequently to check for updates
// the size of a growing file.
export const DEFAULT_POLLING_INTERVAL_IN_MS = 5 * 1000;

// By default, Kelda does everything except central rendering
export const DEFAULT_MONITOR_EDIT_PROXY_WATCH_FOLDER = true;
export const DEFAULT_MONITOR_DIRECT_UPLOAD_WATCH_FOLDER = true;
export const DEFAULT_MONITOR_PREPROCESSING_REQUESTS = true;
export const DEFAULT_MONITOR_INGEST_REQUESTS = true;
export const DEFAULT_MONITOR_CONFORM_REQUESTS = false;

// Default folders to be used by Kelda. The default
// setup expects things to happen within a common directory
// that is mapped into the docker container(s) running Kelda
// into /mnt/production
export const DEFAULT_EDIT_PROXY_WATCH_FOLDER = '/mnt/production/watchfolder';
export const DEFAULT_P2_SINGLE_ITEM_WATCH_FOLDER = '/mnt/production/p2watchfolder';
export const DEFAULT_DIRECT_UPLOAD_WATCH_FOLDER = '/mnt/production/minio_root/uploaded';
export const DEFAULT_SERVER_FOLDER = '/mnt/production/minio_root/mimir_root';
export const DEFAULT_TEMP_FOLDER = '/mnt/production/tmp';
export const DEFAULT_LOG_FOLDER = '/mnt/production/logs';

export const DEFAULT_INGEST_FILE_EXTENSIONS = '.mp4,.mxf,.mov,.wav,.mp3,.jpg,.png';

type IngestConfigWithAllProperties = {
  [Property in keyof Required<IngestStorageConfig>]: IngestStorageConfig[Property]
};
export const ingestStorageConfigDtoPrototype: IngestConfigWithAllProperties = {
  enableS3Acceleration: false,
  id: '',
  isDisabled: false,
  title: '',
  ingestConfigType: 'undefined',
  storageProvider: 's3_native',
  sourceBucket: '',
  sourceKeyPrefix: '',
  sourceBucketCredentials: '',
  sourceEndpoint: '',
  ingestFileExtensions: '',
  ingestFileTags: [],
  ingestIfAnyTagsMatch: false,
  highresFolder: '',
  targetFolder: '',
  targetFolderRemoveSourcePrefix: true,
  requireSidecar: false,
  sidecarParser: '',
  sidecarFileName: '',
  confidence: 90,
  sourceLastModified: new Date().toISOString(),
  analyticConfig: defaultAnalyticConfig(),
  isProxyOnlyIngestConfig: false,
  accessKey: '',
  defaultMetadata: undefined,
  defaultTimedMetadata: undefined,
  scanStorageState: undefined,
  deleteEnabled: false,
  accessType: undefined,
  externalId: createUuid(),
  visibleTo: getDefaultAccessControl(),
  visibleToGroupList: undefined,
  skipProcessing: false,
};

export function getIngestStorageConfigDtoPropertyNames() {
  const names: string[] = [];
  for (const key of Object.keys(ingestStorageConfigDtoPrototype)) {
    names.push(key);
  }
  return names.join(', ');
}

export const createIngestPrototype = (externalId: string) => {
  const configDto = cloneDeep(ingestStorageConfigDtoPrototype);
  configDto.id = `fake_${createUuid()}`;
  configDto.externalId = externalId;
  return configDto;
};

export const isNewConfig = (dto: IngestStorageConfig) => {
  return dto.id.startsWith('fake_');
};

export const EXTERNAL_UPLOAD_INGEST_NAME = 'external_upload_ingest';
export const getUploadLocationIngestId = (tenantId: string) => `${tenantId}-${EXTERNAL_UPLOAD_INGEST_NAME}`;

export interface AWSKeys {
  AWS_ACCESS_KEY_ID: string;
  AWS_SECRET_ACCESS_KEY: string;
}

export const isInstanceOfAWSKeys = (obj: unknown): obj is AWSKeys => {
  if (!isObject(obj)) return false;

  const possibleKeys = obj as Partial<AWSKeys>;
  return isString(possibleKeys.AWS_ACCESS_KEY_ID) && isString(possibleKeys.AWS_SECRET_ACCESS_KEY);
};

export const awsAccessTypes = <const>['assume-role', 'iam-user'];
/**
 * Supported flavors of gaining access to AWS resources in a different account.
 */
export type AwsAccessType = typeof awsAccessTypes[number];

/**
 * Credentials needed to access AWS resources by assuming an IAM role.
 */
export interface RoleBasedAccessKeys {
  /**
   * Full ARN of the IAM role
   */
  iamRoleArn: string;
  /**
   * An unique ID used in the 'trust policy' of the IAM role
   */
  externalId: string;
  /**
   * Optional expiration date for the credentials
   */
  expiration: Date | undefined;
}

export const isInstanceOfRoleBasedAccessKeys = (obj: unknown): obj is RoleBasedAccessKeys => {
  if (!isObject(obj)) return false;

  const possibleKeys = obj as Partial<RoleBasedAccessKeys>;
  return isString(possibleKeys.iamRoleArn) && isString(possibleKeys.externalId);
};

type ResolvedRoleBased = { accessType: 'assume-role' } & RoleBasedAccessKeys;
type ResolvedIamUser = { accessType: 'iam-user' };

/**
 * Interface defining properties available for a fully resolved set of credentials.
 */
export type AwsResolvedCredentials = AwsCredentialIdentity & (ResolvedRoleBased | ResolvedIamUser);

export const isInstanceOfAwsResolvedCredentials = (obj: unknown): obj is AwsResolvedCredentials => {
  if (!isObject(obj)) return false;

  const possibleKeys = obj as Partial<AwsResolvedCredentials>;
  return isString(possibleKeys.accessKeyId) && isString(possibleKeys.secretAccessKey)
    && isString(possibleKeys.accessType) && awsAccessTypes.includes(possibleKeys.accessType as any);
};

export interface AzureKeys {
  AZURE_ACCOUNT_NAME: string;
  AZURE_ACCOUNT_KEY: string;
}

export const isInstanceOfAzureKeys = (obj: unknown): obj is AzureKeys => {
  if (!isObject(obj)) return false;

  const possibleKeys = obj as Partial<AzureKeys>;
  return isString(possibleKeys.AZURE_ACCOUNT_KEY) && isString(possibleKeys.AZURE_ACCOUNT_NAME);
};

export interface GoogleKeys {
  SERVICE_ACCOUNT_CREDENTIALS: string;
}

export const isInstanceOfGoogleKeys = (obj: unknown): obj is GoogleKeys => {
  if (!isObject(obj)) return false;

  const possibleKeys = obj as Partial<GoogleKeys>;
  return isString(possibleKeys.SERVICE_ACCOUNT_CREDENTIALS);
};

export const getIngestSourceKey = (srcBucket: string, srcKey: string, tenantId: string) =>
  `${srcBucket}_${srcKey}_${tenantId}`;

export interface BucketAccessInfo {
  accessKey: string;
  awsAccountId?: string;
}

export const personIngestFileTypes = '.jpg,.jpeg,.jpe,.json,.png';
